import { EntityTypeEnum } from '../enums/entity.enum';
import {
  CELL_IMPORT_STRATEGY_CONFIG,
  CELL_VALUE_TYPE_CONFIG,
  COMMON_EXCEL_WORKBOOK_CONFIG,
  FULL_SHEET_PROTECTION_CONFIG,
  SHEET_STATE_CONFIG,
  TWorkbookConfig,
  UTILS,
  WORKBOOK_IMPORT_STRATEGY_CONFIG,
} from '@aksia/infrastructure';
import {
  InvestorType,
  InvestorConcentration,
  InternalCapital,
  Liquidity,
  MarketCapNav,
  AdministratorVerifications,
  CounterpartiesCompact,
  Staffing,
  Ownership,
} from '../classes/datacize-stream/datacize-stream';

export const DATACIZE_STREAMS = {
  INVESTOR_TYPE: 'investorType',
  INVESTOR_CONCENTRATION: 'investorConcentration',
  INTERNAL_CAPITAL: 'internalCapital',
  LIQUIDITY: 'liquidity',
  MARKET_CAP_NAV: 'marketCapNav',
  ADMINISTRATOR_VERIFICATIONS: 'administratorVerifications',
  COUNTERPARTIES: 'counterpartiesCompact',
  STAFFING: 'staffing',
  OWNERSHIP: 'ownership',
} as const;

export const DATACIZE_STREAM_GROUPS = {
  DATACIZE_STREAMS_INVESTMENTVEHICLE:
    'investorType,investorConcentration,internalCapital,liquidity,marketCapNav,administratorVerifications,counterpartiesCompact',
  DATACIZE_STREAMS_COMPANY: 'staffing,ownership',
} as const;

export const DATACIZE_STREAM_REQUEST_MODE = {
  HISTORICAL: 'HISTORICAL',
  UPDATE: 'UPDATE',
} as const;

export const DATACIZE_STREAM_URIS = {
  INVESTMENT_VEHICLE: {
    HISTORICAL:
      'periodicdata/fundtimeseriesdata/alldatagroups/{entityId}/stream/allbygroup',
    UPDATE:
      'periodicdata/fundtimeseriesdata/alldatagroups/{entityId}/stream/edit',
  },
  COMPANY: {
    HISTORICAL:
      'periodicdata/managementcompanytimeseriesdata/alldatagroups/{entityId}/stream/allbygroup',
    UPDATE:
      'periodicdata/managementcompanytimeseriesdata/alldatagroups/{entityId}/stream/edit',
  },
  URI: (
    entityId: number,
    entityTypeId: EntityTypeEnum,
    requestType: (typeof DATACIZE_STREAM_REQUEST_MODE)[keyof typeof DATACIZE_STREAM_REQUEST_MODE],
  ) => {
    let uri = '';

    if (entityTypeId === EntityTypeEnum.ManagementCompany) {
      uri = DATACIZE_STREAM_URIS.COMPANY[requestType];
    } else {
      uri = DATACIZE_STREAM_URIS.INVESTMENT_VEHICLE[requestType];
    }

    uri = uri
      ?.replace('{entityId}', entityId.toString())
      ?.replace('{entityTypeId}', entityTypeId.toString());
    return uri;
  },
};

export const DATACIZE_STREAM_INSTANCES = {
  investorType: InvestorType,
  investorConcentration: InvestorConcentration,
  internalCapital: InternalCapital,
  liquidity: Liquidity,
  marketCapNav: MarketCapNav,
  administratorVerifications: AdministratorVerifications,
  counterpartiesCompact: CounterpartiesCompact,
  staffing: Staffing,
  ownership: Ownership,
} as const;

export const DATACIZE_EXCEL_WORKBOOK_CONFIG: TWorkbookConfig = {
  ...COMMON_EXCEL_WORKBOOK_CONFIG,
  category: 'Datacize Streams',
  subject: '1.0',
  description: 'Import / Export file for datacize streams.',
  manager: 'ODD@aksia.com',
  importStrategy: WORKBOOK_IMPORT_STRATEGY_CONFIG.SERVER,
  sheets: [
    {
      sheetName: 'Data Request',
      state: SHEET_STATE_CONFIG.VISIBLE,
      tables: [
        {
          name: 'Staffing_Table',
          ref: 'C221',
          headerRow: true,
          columns: [
            { name: 'Investment---Number of Hires---', filterButton: false },
            {
              name: 'Non-Investment---Number of Hires---',
              filterButton: false,
            },
            { name: 'Investment---Number of Deps---', filterButton: false },
            { name: 'Non-Investment---Number of Deps---', filterButton: false },
            { name: 'Investment---Total---', filterButton: false },
            { name: 'Non-Investment---Total---', filterButton: false },
            { name: 'Total', filterButton: false },
          ],
        },
      ],
      data: [],
      conditionalFormatting: [],
      protection: FULL_SHEET_PROTECTION_CONFIG,
      cells: [
        {
          cell: 'InvestorType_PensionFunds',
          field: 'pensionFunds',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorType_SovereignWeathFunds',
          field: 'sovereignWealthFunds',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorType_EndowmentsFoundations',
          field: 'endowmentsFoundations',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorType_Insurance',
          field: 'insurance',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorType_FamilyOffices',
          field: 'familyOffices',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorType_FundofFunds',
          field: 'fundOfFunds',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorType_PrivateBanks',
          field: 'privateBanks',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorType_Other',
          field: 'other',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_TYPE,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorConcentration_Top1',
          field: 'topOne',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_CONCENTRATION,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorConcentration_Top3',
          field: 'topThree',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_CONCENTRATION,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorConcentration_Top5',
          field: 'topFive',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_CONCENTRATION,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InvestorConcentration_Top10',
          field: 'topTen',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INVESTOR_CONCENTRATION,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InternalCapital_Firm',
          field: 'firm',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INTERNAL_CAPITAL,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'InternalCapital_Fund',
          field: 'fund',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.INTERNAL_CAPITAL,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        ...[1, 2, 3, 4, 5].map((i) => ({
          cell: `LiquidityAnalysis_To${i - 1}`,
          field: 'from',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.LIQUIDITY,
        })),
        ...[1, 2, 3, 4, 5].map((i) => ({
          cell: `LiquidityAnalysis_ToDate${i}`,
          field: 'fromType',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.LIQUIDITY,
        })),
        ...[1, 2, 3, 4, 5].map((i) => ({
          cell: `LiquidityAnalysis_To${i}`,
          field: 'to',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.LIQUIDITY,
        })),
        ...[1, 2, 3, 4, 5].map((i) => ({
          cell: `LiquidityAnalysis_ToDate${i}`,
          field: 'ToType',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.LIQUIDITY,
        })),
        ...[1, 2, 3, 4, 5].map((i) => ({
          cell: `LiquidityAnalysis_PercentOfPortfolio${i}`,
          field: 'percentageOfPortfolio',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.LIQUIDITY,
          onImport: (value: number) =>
            Number.isFinite(value) ? value : undefined,
        })),
        {
          cell: 'MarketCapBreakdown_Long_Mega',
          field: 'megaLong',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Long_Large',
          field: 'largeLong',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Long_Mid',
          field: 'midLong',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Long_Small',
          field: 'smallLong',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Long_Micro',
          field: 'microLong',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Long_IndicesOther',
          field: 'indicesOtherLong',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Short_Mega',
          field: 'megaShort',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Short_Large',
          field: 'largeShort',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Short_Mid',
          field: 'midShort',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Short_Small',
          field: 'smallShort',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Short_Micro',
          field: 'microShort',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        {
          cell: 'MarketCapBreakdown_Short_IndicesOther',
          field: 'indicesOtherShort',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.MARKET_CAP_NAV,
          onImport: (value) => (Number.isFinite(value) ? value : undefined),
        },
        ...[1, 2, 3, 4, 5, 6].map((i) => ({
          cell: `Counterparty_Counterparty${i}`,
          field: 'counterpartyId',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.COUNTERPARTIES,
          onImport: 'getCounterPartyId',
        })),
        ...[1, 2, 3, 4, 5, 6].map((i) => ({
          cell: `Counterparty_FinancialInstitution${i}`,
          field: 'typeIds',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.COUNTERPARTIES,
          onImport: 'getTypeIds',
        })),
        ...[1, 2, 3, 4, 5, 6].map((i) => ({
          cell: `Counterparty_Exposure${i}`,
          field: 'exposure',
          fieldIndex: i - 1,
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.COUNTERPARTIES,
        })),
        {
          cell: 'As Of',
          field: 'asOfDate',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
          onImport: (value: string | number) =>
            typeof value === 'number'
              ? UTILS.DATE.toLocalISOString(new Date(value, 11, 31))
              : value?.trim() === 'YTD'
                ? 'YTD'
                : undefined,
        },
        {
          cell: 'Investment---Number of Hires---',
          field: 'investmentHires',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
        },
        {
          cell: 'Non-Investment---Number of Hires---',
          field: 'nonInvestmentHires',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
        },
        {
          cell: 'Investment---Number of Deps---',
          field: 'investmentDepartures',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
        },
        {
          cell: 'Non-Investment---Number of Deps---',
          field: 'nonInvestmentDepartures',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
        },
        {
          cell: 'Investment---Total---',
          field: 'investmentEmployees',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
        },
        {
          cell: 'Non-Investment---Total---',
          field: 'nonInvestmentEmployees',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
        },
        {
          cell: 'Total',
          field: 'totalStaff',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.TABLE_STRUCTURE,
          mapName: 'Staffing_Table',
        },
        {
          cell: 'Manager_Ownership_InternalEquity',
          field: 'internalEquity',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.OWNERSHIP,
        },
        {
          cell: 'Manager_Ownership_InternalVoting',
          field: 'internalVoting',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.OWNERSHIP,
        },
        {
          cell: 'Manager_Ownership_ExternalEquity',
          field: 'externalEquity',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.OWNERSHIP,
        },
        {
          cell: 'Manager_Ownership_ExternalVoting',
          field: 'externalVoting',
          valueType: CELL_VALUE_TYPE_CONFIG.NUMBER,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: DATACIZE_STREAMS.OWNERSHIP,
        },
      ],
    },
    {
      sheetName: 'ODD Questionnaire',
      state: SHEET_STATE_CONFIG.VISIBLE,
      data: [],
      conditionalFormatting: [],
      protection: FULL_SHEET_PROTECTION_CONFIG,
      cells: [
        {
          cell: 'ServiceProviders_RegulatoryRegistrations',
          field: 'regulatoryRegistrations',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([['CategoryName', 'Regulatory Registrations']]),
          onImport: 'getTags',
        },
        {
          cell: 'ServiceProvider_Administrators',
          field: 'administrators',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([['CategoryName', 'Administrators']]),
          onImport: 'getTags',
        },
        {
          cell: 'ServiceProvider_LegalCounsel',
          field: 'legalCounsel',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([['CategoryName', 'Legal Counsel']]),
          onImport: 'getTags',
        },
        {
          cell: 'ServiceProvider_ComplianceConsultant',
          field: 'complianceConsultant',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([['CategoryName', 'Compliance Consultants']]),
          onImport: 'getTags',
        },
        {
          cell: 'ServiceProvider_ITServiceProvider',
          field: 'itServiceProvider',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([['CategoryName', 'IT Service Providers']]),
          onImport: 'getTags',
        },
        {
          cell: 'ServiceProvider_OMSPMS',
          field: 'omspms',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([
            ['Third Party System', 'Third-party System'],
            ['CategoryName', 'OMS/PMS'],
          ]),
          onImport: 'getTags',
        },
        {
          cell: 'ServiceProvider_AccountingSystem',
          field: 'accountingSystem',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([
            ['Third Party System', 'Third-party System'],
            ['CategoryName', 'Accounting System'],
          ]),
          onImport: 'getTags',
        },
      ],
    },
    {
      sheetName: 'Technology and AI',
      state: SHEET_STATE_CONFIG.VISIBLE,
      data: [],
      conditionalFormatting: [],
      protection: FULL_SHEET_PROTECTION_CONFIG,
      cells: [
        {
          cell: 'ServiceProvider_ITServiceProvider',
          field: 'itServiceProvider',
          valueType: CELL_VALUE_TYPE_CONFIG.STRING,
          mapStrategy: CELL_IMPORT_STRATEGY_CONFIG.NAMED_CELL,
          mapName: 'Service Providers',
          valueMap: new Map([['CategoryName', 'IT Service Providers']]),
          onImport: 'getTags',
        },
      ],
    },
  ],
};
